import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft,
    Mail,
    Phone,
    Building,
    CalendarDays,
    UserCircle,
    BookOpen,
    AlertCircle,
    Clock,
    CheckCircle,
} from 'lucide-react';
import { employeeService } from '../../services/employeeService';
import { EmployeeDetails } from '../../types';

const getProgressColor = (progress: number) => {
    if (progress === 0) return 'bg-red-500';
    if (progress < 40) return 'bg-amber-500';
    if (progress < 100) return 'bg-primary-500';
    return 'bg-green-600';
};

const getStatusVisuals = (status: string) => {
    switch (status) {
        case 'Completed':
            return { color: 'text-green-600', bgColor: 'bg-green-100', icon: <CheckCircle size={16} /> };
        case 'In Progress':
            return { color: 'text-primary-600', bgColor: 'bg-primary-100', icon: <Clock size={16} /> };
        case 'Pending':
        case 'Not Started':
            return { color: 'text-amber-600', bgColor: 'bg-amber-100', icon: <Clock size={16} /> };
        case 'Overdue':
            return { color: 'text-red-600', bgColor: 'bg-red-100', icon: <AlertCircle size={16} /> };
        default:
            return { color: 'text-slate-500', bgColor: 'bg-slate-100', icon: <Clock size={16} /> };
    }
};

const formatDate = (dateString: string, includeTime = false) => {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }
    return new Date(dateString).toLocaleDateString(undefined, options);
};

export function AdminEmployeeDetailPage() {
    const navigate = useNavigate();
    const { employeeId } = useParams();
    const [employee, setEmployee] = useState<EmployeeDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchEmployeeDetails = async () => {
            try {
                setError(null);
                const response = await employeeService.getEmployeeDetails(employeeId!);
                setEmployee(response);
            } catch (error) {
                console.error('Error fetching employee details:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch employee details');
            } finally {
                setIsLoading(false);
            }
        };

        if (employeeId) {
            fetchEmployeeDetails();
        } else {
            setError('No employee ID provided');
            setIsLoading(false);
        }
    }, [employeeId]);

    if (isLoading) {
        return (
            <div className="p-6 flex justify-center items-center min-h-screen bg-background">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center min-h-screen bg-background">
                <UserCircle size={64} className="mx-auto text-slate-400 mb-4" />
                <h2 className="text-2xl font-semibold text-foreground">Error Loading Employee</h2>
                <p className="text-slate-500 mb-4">{error}</p>
                <button
                    onClick={() => navigate('/admin/employees')}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center mx-auto"
                >
                    <ChevronLeft size={18} className="mr-2" />
                    Back to Employee List
                </button>
            </div>
        );
    }

    if (!employee) {
        return (
            <div className="p-6 text-center min-h-screen bg-background">
                <UserCircle size={64} className="mx-auto text-slate-400 mb-4" />
                <h2 className="text-2xl font-semibold text-foreground">Employee Not Found</h2>
                <p className="text-slate-500">The requested employee data could not be loaded.</p>
                <button
                    onClick={() => navigate('/admin/employees')}
                    className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center mx-auto"
                >
                    <ChevronLeft size={18} className="mr-2" />
                    Back to Employee List
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            {/* Header and Back Button */}
            <div className="mb-6">
                <button
                    onClick={() => navigate('/admin/employees')}
                    className="inline-flex items-center px-4 py-2.5 bg-white border border-slate-300 rounded-lg shadow-sm hover:shadow-md hover:border-primary-300 text-foreground hover:text-primary-600 transition-all duration-200 group"
                >
                    <ChevronLeft size={18} className="mr-2 group-hover:-translate-x-0.5 transition-transform duration-200" />
                    <span className="font-medium">Back to Employee List</span>
                </button>
            </div>

            {/* Employee Profile Header */}
            <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
                <div className="flex flex-col sm:flex-row items-center">
                    <div className="w-24 h-24 rounded-full mr-0 sm:mr-6 mb-4 sm:mb-0 border-4 border-slate-200 bg-primary-100 flex items-center justify-center">
                        <span className="text-2xl font-medium text-primary-600">
                            {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                    </div>
                    <div className="text-center sm:text-left">
                        <h1 className="text-3xl font-bold text-foreground">{employee.name}</h1>
                        <p className="text-primary-600 font-medium text-lg">{employee.role}</p>
                        <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-1 text-sm text-slate-600">
                            <span className="flex items-center"><Mail size={14} className="mr-1.5 text-slate-400" /> {employee.email}</span>
                            {employee.phone && <span className="flex items-center"><Phone size={14} className="mr-1.5 text-slate-400" /> {employee.phone}</span>}
                        </div>
                    </div>
                </div>
                <div className="mt-6 border-t border-slate-200 pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-start">
                        <CalendarDays size={16} className="mr-2 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div><strong className="text-slate-600">Join Date:</strong> {formatDate(employee.created_at)}</div>
                    </div>
                    <div className="flex items-start">
                        <Building size={16} className="mr-2 text-slate-400 flex-shrink-0 mt-0.5" /> 
                        <div><strong className="text-slate-600">Role:</strong> Direct Support Professional</div>
                    </div>
                    <div className="flex items-start">
                        <UserCircle size={16} className="mr-2 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div><strong className="text-slate-600">Status:</strong> <span className="capitalize">{employee.status}</span></div>
                    </div>
                </div>
            </div>

            {/* Curriculums Section */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground flex items-center">
                        <BookOpen size={20} className="mr-2 text-primary-500" /> Training Curriculums
                    </h2>
                    <div className="mt-2 sm:mt-0 flex items-center text-sm">
                        <CheckCircle size={16} className="mr-2 text-slate-400" />
                        <span className="text-slate-600">Total Completed:</span>
                        <span className="ml-1 text-slate-500">
                            ({employee.totalCourses > 0 ? Math.round((employee.totalCompletedCourses / employee.totalCourses) * 100) : 0}%)
                        </span>
                        <span className="ml-1 font-semibold text-primary-600">
                            {employee.totalCompletedCourses}/{employee.totalCourses} 
                        </span>
                    </div>
                </div>
                {employee.curriculums && employee.curriculums.length > 0 ? (
                    <div className="space-y-6">
                        {employee.curriculums.map(curriculum => (
                            <div key={curriculum.id} className="border border-slate-200 rounded-lg p-4">
                                <div className="flex justify-between items-center mb-3">
                                    <div>
                                        <h3 className="text-lg font-medium text-foreground">{curriculum.name}</h3>
                                        <p className="text-sm text-slate-500">
                                            Assigned: {formatDate(curriculum.assignedDate)} • 
                                            Year: {curriculum.courseYear}
                                        </p>
                                    </div>
                                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                        curriculum.completed 
                                            ? 'bg-green-100 text-green-600' 
                                            : 'bg-amber-100 text-amber-600'
                                    }`}>
                                        {curriculum.completed ? 'Completed' : 'In Progress'}
                                    </span>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="flex justify-between text-sm text-slate-600 mb-1">
                                        <span>Curriculum Progress</span>
                                        <span>{curriculum.numberOfCompletedCourses}/{curriculum.numberOfCourses} courses</span>
                                    </div>
                                    <div className="w-full bg-slate-200 rounded-full h-2">
                                        <div
                                            className={`${getProgressColor(curriculum.numberOfCourses > 0 ? (curriculum.numberOfCompletedCourses / curriculum.numberOfCourses) * 100 : 0)} h-2 rounded-full transition-all duration-300 ease-in-out`}
                                            style={{ width: `${curriculum.numberOfCourses > 0 ? (curriculum.numberOfCompletedCourses / curriculum.numberOfCourses) * 100 : 0}%` }}
                                        />
                                    </div>
                                </div>
                                
                                {curriculum.courses && curriculum.courses.length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className="text-sm font-medium text-foreground">Courses:</h4>
                                        <div className="grid gap-3">
                                            {curriculum.courses.map(course => {
                                                const statusVisuals = getStatusVisuals(course.status);
                                                return (
                                                    <div key={course.id} className="p-3 rounded-lg border border-slate-100 bg-slate-50">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <h5 className="font-medium text-foreground text-sm">{course.name}</h5>
                                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusVisuals.bgColor} ${statusVisuals.color}`}>
                                                                {course.status}
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-200 rounded-full h-1.5 mb-1">
                                                            <div
                                                                className={`${getProgressColor(course.progress)} h-1.5 rounded-full transition-all duration-300 ease-in-out`}
                                                                style={{ width: `${course.progress}%` }}
                                                            />
                                                        </div>
                                                        <div className="flex justify-between items-center text-xs text-slate-500">
                                                            <span>Progress: {course.progress}%</span>
                                                            {course.dueDate && (
                                                                <span className={`flex items-center ${new Date(course.dueDate) < new Date() ? 'text-red-500 font-semibold' : ''}`}>
                                                                    {new Date(course.dueDate) < new Date() ? <AlertCircle size={10} className="mr-1" /> : <CalendarDays size={10} className="mr-1" />}
                                                                    {formatDate(course.dueDate)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-slate-500">No curriculums currently assigned.</p>
                )}
            </div>
        </div>
    );
} 